import classNames from 'classnames'
import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { Loading } from 'router/Loading'
import { useActions } from 'store/hooks'

import { CreateOrder } from './CreateOrder'
import { usePreselectProduct } from './hooks/usePreselectProduct'
import { useSendForm } from './hooks/useSendForm'
import { orderFormSpace } from './namespaces/orderFormSpace'
import { OrderStepConfirm } from './orderConfirm/OrderStepConfirm'
import { OrderStepAddress } from './orderStepAddress'
import { OrderStepBankDetails } from './orderStepBankDetails/OrderStepBankDetails'
import { OrderStepDate } from './orderStepDate'
import { OrderStepSelectProdParams } from './orderStepSelectProdParams'
import { OrderStepSelectProdType } from './orderStepSelectProdType'
import style from './ProductOrder.module.scss'

type StepItem = {
  StepView: React.ComponentType<{
    completeStep: (isLastStep: boolean) => void
    handlePrevious: (isFirstStep: boolean) => void
  }>
  label: string
  id: string
}

const BASE_STEPS: StepItem[] = [
  {
    StepView: OrderStepSelectProdType,
    label: 'Выбор типа изделия',
    id: 'type',
  },
  {
    StepView: OrderStepSelectProdParams,
    label: 'Уточнение параметров',
    id: 'props',
  },
  { StepView: OrderStepAddress, label: 'Адрес объекта', id: 'address' },
  { StepView: OrderStepConfirm, label: 'Подтверждение', id: 'confirm' },
]

const DATE_STEP: StepItem = {
  StepView: OrderStepDate,
  label: 'Дата и время диагностики',
  id: 'date',
}

const BANK_STEP: StepItem = {
  StepView: OrderStepBankDetails,
  label: 'Банковские реквизиты',
  id: 'bankDetails',
}

function buildSteps(
  orderType: orderFormSpace.FormType,
  isCompany: boolean,
): StepItem[] {
  let steps: StepItem[]

  switch (orderType) {
    case orderFormSpace.FormType.new:
    case orderFormSpace.FormType.newCompany:
      steps = BASE_STEPS
      break
    case orderFormSpace.FormType.service:
      steps = [
        ...BASE_STEPS.slice(0, -1),
        DATE_STEP,
        BASE_STEPS[BASE_STEPS.length - 1],
      ]
      break
    default:
      return []
  }

  if (!isCompany) return steps

  return [...steps.slice(0, -1), BANK_STEP, steps[steps.length - 1]]
}

interface IProductOrder {
  orderType: orderFormSpace.FormType
  forceUserType?: orderFormSpace.UserType
}

// заявка продукции ("/productorder")
export const ProductOrder: FC<IProductOrder> = ({
  orderType,
  forceUserType,
}) => {
  const { setCurrentEditForm, setUserTypeOrder } = useActions()

  const [searchParams] = useSearchParams()
  const productId = searchParams.get('productId')
    ? Number(searchParams.get('productId'))
    : null

  const isCompany = forceUserType === 'company'

  useEffect(() => {
    setCurrentEditForm(orderType)
    if (forceUserType) setUserTypeOrder(forceUserType)
  }, [])

  const isReady = usePreselectProduct(orderType, productId)

  const methods = useForm({ defaultValues: { prodTypes: [] } })
  const { sendForm } = useSendForm()

  const onSubmit = () => sendForm()

  if (!isReady) return <Loading />

  return (
    <div className={classNames('container', style.createOrderPage)}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className={style.createOrderForm}
        >
          <CreateOrder
            stepArray={buildSteps(orderType, isCompany)}
            handleSubmit={onSubmit}
            orderType={orderType}
          />
        </form>
      </FormProvider>
    </div>
  )
}
